import React, { useState, useCallback, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { PlusCircle, Edit, Trash2, FileText } from 'react-feather';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, FormGroup, Label, Input } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';
import LicensePlateDataModal from '../CustomerDetailModals/LicensePlateDataModal';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import { confirmDelete, createCallFormFromLicensePlate } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import axios from 'axios';

const LicensePlateInfo = ({ licensePlates = [], insuranceCompanies = [], onDataChange, customerId }) => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedItem, setSelectedItem] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [clearRows, setClearRows] = useState(false);

    // Personel seçimi için yeni state'ler
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [licensePlateToAssign, setLicensePlateToAssign] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [isCreatingForm, setIsCreatingForm] = useState(false);

    // Form bilgileri için yeni state'ler
    const [formData, setFormData] = useState({
        accidentDate: '',
        ourFaultRate: '',
        willLorBeWorked: false
    });

    const employeeApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/user';

    // Personel listesini çekme fonksiyonu (JobPool'dan uyarlandı)
    const fetchEmployees = useCallback(async () => {
        try {
            const response = await axios.get(`${employeeApiUrl}?userRole=documentrequest`);
            let users = [];
            if (response.data?.isSuccess && Array.isArray(response.data.data?.items)) {
                users = response.data.data.items;
            } else if (Array.isArray(response.data?.data)) {
                users = response.data.data;
            } else if (Array.isArray(response.data)) {
                users = response.data;
            } else {
                console.error("Unexpected employee list response structure:", response.data);
                toast.error("Personel listesi alınırken beklenmedik yanıt yapısı.");
                setEmployeeList([]);
                return;
            }

            const formattedUsers = users
                .filter(user => user.id && user.firstName && user.lastName)
                .map(user => ({
                    id: user.id,
                    name: `${user.firstName} ${user.lastName}`
                }));

            setEmployeeList(formattedUsers);
        } catch (error) {
            console.error("Error fetching employees:", error);
            toast.error("Personel listesi alınırken bir hata oluştu.");
            setEmployeeList([]);
        }
    }, [employeeApiUrl]);

    // Bileşen yüklendiğinde personel listesini çek
    useEffect(() => {
        fetchEmployees();
    }, [fetchEmployees]);

    // Personel seçim modalını açma/kapama
    const toggleAssignModal = (licensePlate = null) => {
        if (licensePlate) {
            setSelectedUserId('');
            setLicensePlateToAssign(licensePlate);
            // Form verilerini sıfırla
            setFormData({
                accidentDate: '',
                ourFaultRate: '',
                willLorBeWorked: false
            });
        } else {
            setLicensePlateToAssign(null);
        }
        setAssignModalOpen(!assignModalOpen);
    };

    // Form input değişikliklerini handle etme
    const handleFormInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (type === 'number' ? parseInt(value, 10) || 0 : value)
        }));
    };

    // Kaza oluşturma işlemi
    const handleCreateCallForm = async () => {
        if (!licensePlateToAssign) {
            toast.error("Plaka bilgisi bulunamadı.");
            return;
        }
        if (!selectedUserId) {
            toast.error("Lütfen bir personel seçin.");
            return;
        }
        if (!formData.accidentDate) {
            toast.error("Kaza tarihi gereklidir.");
            return;
        }
        if (formData.ourFaultRate === '') {
            toast.error("Kusur oranı gereklidir.");
            return;
        }
        if (formData.willLorBeWorked === null || formData.willLorBeWorked === undefined) {
            toast.error("Hak Mahrumiyeti Çalışılacak Mı? seçimi gereklidir.");
            return;
        }

        setIsCreatingForm(true);

        try {
            // AccidentDate'i ISO format'a çevir
            const accidentDateISO = new Date(formData.accidentDate).toISOString();

            const response = await createCallFormFromLicensePlate({
                licensePlate: licensePlateToAssign.id.toString(),
                appointedUser: selectedUserId,
                accidentDate: accidentDateISO,
                ourFaultRate: parseInt(formData.ourFaultRate, 10),
                willLorBeWorked: formData.willLorBeWorked
            });

            console.log("Create call form response:", response);
            toast.success('Çağrı formu başarıyla oluşturuldu!');
            toggleAssignModal();

            // Veri yenileme
            if (onDataChange) {
                onDataChange();
            }
        } catch (error) {
            console.error("Error creating call form:", error);
            // Error handling zaten API method içinde yapılıyor
        } finally {
            setIsCreatingForm(false);
        }
    };

    // Tarih formatını düzenleyen yardımcı fonksiyon
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return isNaN(date) ? '' : date.toLocaleDateString('tr-TR');
    };

    // Sigorta bilgilerini formatlamak için basitleştirilmiş fonksiyonlar
    // Sadece sigorta numarasını göster
    const formatTrafficInsuranceInfo = (policy) => {
        if (!policy) return '';
        return policy.trafficInsuranceNumber || '';
    };

    const formatCascoInsuranceInfo = (policy) => {
        if (!policy) return '';
        return policy.cascoInsuranceNumber || '';
    };

    const columns = [
        { name: 'Plaka Numarası', selector: row => row.licensePlateNumber || '', sortable: true },
        { name: 'Plaka Durumu', selector: row => row.licensePlateStatus || '', sortable: true },
        { name: 'Şasi Numarası', selector: row => row.carchassisNumber || '', sortable: true },
        { name: 'Araç Markası', selector: row => row.carBrand || '', sortable: true },
        { name: 'Araç Modeli', selector: row => row.carModel || '', sortable: true },
        { name: 'Araç Yılı', selector: row => row.carYear || '', sortable: true },
        { name: 'Araç Sahibi', selector: row => row.carOwner || '', sortable: true },
        {
            name: 'Trafik Sigortası',
            selector: row => formatTrafficInsuranceInfo(row.trafficInsurancePolicy),
            sortable: true,
        },
        {
            name: 'Kasko Sigortası',
            selector: row => formatCascoInsuranceInfo(row.cascoInsurancePolicy),
            sortable: true,
        },
        { name: 'Açıklama', selector: row => row.description || '', sortable: true },
        {
            name: 'Kaza Oluştur',
            cell: (row) => {
                return row.hasPowerOfAttorney ? (
                    <div
                        className="btn btn-outline-success d-flex align-items-center justify-content-center"
                        onClick={() => handleCreateForm(row)}
                        title="Kaza Oluştur"
                    >
                        <FileText size={18} />
                    </div>
                ) : '-';
            },
            ignoreRowClick: true,
            right: true,
            button: true,
        },
        {
            name: 'İşlemler',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => handleEdit(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
        },
    ];

    const handleRowSelected = state => {
        setSelectedRows(state.selectedRows);
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedItem(null);
        setModalOpen(true);
    };

    const handleEdit = (row) => {
        setModalMode('modify');
        setSelectedItem(row);
        setModalOpen(true);
    };

    const handleCreateForm = (row) => {
        console.log('Kaza Oluşturuluyor:', row);
        // Personel seçim modalını aç
        toggleAssignModal(row);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <H5>Plakalar</H5>
                <div className="d-flex">
                    <div className="btn btn-outline-primary ms-2 d-flex align-items-center" onClick={openAddModal}>
                        <PlusCircle className="me-1" />
                        Plaka Ekle
                    </div>
                    {selectedRows.length > 0 && (
                        <div className="btn btn-outline-danger ms-2 d-flex align-items-center" onClick={() => setDeleteModal(true)}>
                            <Trash2 size={18} className="me-1" />
                            Seçilenleri Sil
                        </div>
                    )}
                </div>
            </div>
            {licensePlates.length === 0 ? (
                <p>Müşteriye ait plaka bulunamadı.</p>
            ) : (
                <DataTable
                    columns={columns}
                    data={licensePlates}
                    pagination
                    selectableRows
                    onSelectedRowsChange={handleRowSelected}
                    clearSelectedRows={clearRows}
                />
            )}

            <LicensePlateDataModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(!modalOpen)}
                mode={modalMode}
                selectedLicensePlate={selectedItem}
                refreshData={onDataChange}
                customerId={customerId}
                insuranceCompanies={insuranceCompanies}
            />

            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={() => setDeleteModal(!deleteModal)}
                itemsToDelete={selectedRows}
                displayKey="licensePlateNumber"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/licenseplate',
                        selectedItems: selectedRows,
                        toggleModalCallback: () => setDeleteModal(false),
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearRows(!clearRows);
                        },
                        refreshDataCallback: onDataChange,
                    })
                }
            />

            <Modal isOpen={assignModalOpen} toggle={() => toggleAssignModal()} centered>
                <ModalHeader toggle={() => toggleAssignModal()}>Çağrı Formu Oluştur</ModalHeader>
                <ModalBody>
                    <FormGroup>
                        <Label for="employeeSelect">Atanacak Personel <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            name="employeeSelect"
                            id="employeeSelect"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}
                        >
                            <option value="">-- Personel Seçiniz --</option>
                            {employeeList.length > 0 ? (
                                employeeList.map(emp => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.name}
                                    </option>
                                ))
                            ) : (
                                <option value="" disabled>Personel listesi yükleniyor veya boş...</option>
                            )}
                        </Input>
                    </FormGroup>

                    <FormGroup>
                        <Label for="accidentDate">Kaza Tarihi <span className="text-danger">*</span></Label>
                        <Input
                            type="datetime-local"
                            name="accidentDate"
                            id="accidentDate"
                            value={formData.accidentDate}
                            onChange={handleFormInputChange}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label for="ourFaultRate">
                            Müvekkil Kusur Oranı <span className="text-danger">*</span>
                        </Label>
                        <div className="input-group">
                            <Input
                                type="text"
                                id="ourFaultRate"
                                name="ourFaultRate"
                                value={formData.ourFaultRate || ''}
                                onChange={(e) => {
                                    let value = e.target.value.replace(/[^0-9]/g, '');
                                    // 100'den büyük değer girilmesini engelle
                                    if (value && parseInt(value) > 100) {
                                        value = '100';
                                    }
                                    setFormData(prev => ({
                                        ...prev,
                                        ourFaultRate: value
                                    }));
                                }}
                                placeholder="0-100"
                            />
                            <span className="input-group-text">%</span>
                        </div>
                    </FormGroup>

                    <FormGroup className="mb-3">
                        <Label className="form-label">
                            Hak Mahrumiyeti Çalışılacak Mı? <span className="text-danger">*</span>
                        </Label>
                        <div className="d-flex">
                            <div className="radio radio-success me-3 d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedTrue"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value={true}
                                    checked={formData.willLorBeWorked === true}
                                    onChange={() => setFormData(prev => ({ ...prev, willLorBeWorked: true }))}
                                />
                                <Label for="willLorBeWorkedTrue" className="ms-1 mb-0">
                                    Evet
                                </Label>
                            </div>
                            <div className="radio radio-danger d-flex align-items-center">
                                <Input
                                    id="willLorBeWorkedFalse"
                                    type="radio"
                                    name="willLorBeWorked"
                                    value={false}
                                    checked={formData.willLorBeWorked === false}
                                    onChange={() => setFormData(prev => ({ ...prev, willLorBeWorked: false }))}
                                />
                                <Label for="willLorBeWorkedFalse" className="ms-1 mb-0">
                                    Hayır
                                </Label>
                            </div>
                        </div>
                    </FormGroup>

                    {licensePlateToAssign && (
                        <div className="d-flex align-items-center mt-4 mb-3">
                            <div className="flex-shrink-0 me-4">
                                <i className="fa fa-car" style={{ fontSize: '20px' }}></i>
                            </div>
                            <div>
                                <strong>Seçilen Plaka:</strong> {licensePlateToAssign.licensePlateNumber}
                                <br />
                                {licensePlateToAssign.carBrand} {licensePlateToAssign.carModel} {licensePlateToAssign.carYear}
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="primary"
                        onClick={handleCreateCallForm}
                        disabled={
                            !selectedUserId ||
                            !formData.accidentDate ||
                            formData.ourFaultRate === '' ||
                            formData.willLorBeWorked === null ||
                            formData.willLorBeWorked === undefined ||
                            isCreatingForm
                        }
                    >
                        {isCreatingForm ? 'Oluşturuluyor...' : 'Kaza Oluştur'}
                    </Button>{' '}
                    <Button color="secondary" onClick={() => toggleAssignModal()} disabled={isCreatingForm}>
                        İptal
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default LicensePlateInfo;
